import { useState, useEffect } from 'react';
import { useAuth, authService, createAuthenticatedUrl } from '@bri/shared-components';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { CreateProjectModal } from '@/components/project/CreateProjectModal';
import { Button } from '@/components/ui/Button';
import { 
  FolderIcon, 
  PlusIcon,
  CogIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { projectService, PROJECT_TYPES, type Project } from '@/lib/services/projectService';
import { formatDateTime, formatRelativeTime, getErrorMessage } from '@/lib/utils';

// Get the Qual Analyzer URL from environment
const QUAL_ANALYZER_URL = import.meta.env.VITE_QUAL_ANALYZER_URL || 'http://localhost:3001';

interface SuccessResponse {
  success: boolean;
  error?: string;
}

interface CleanupResponse extends SuccessResponse {
  data: {
    totalCleaned: number;
    message: string;
  };
}

interface FixMembershipsResponse extends SuccessResponse {
  data: {
    fixedCount: number;
    totalMemberships: number;
  };
}

function isSuccessResponse(value: unknown): value is SuccessResponse {
  return typeof value === 'object' && value !== null && 'success' in value;
}

function isCleanupResponse(value: unknown): value is CleanupResponse {
  return (
    isSuccessResponse(value) &&
    'data' in value &&
    typeof value.data === 'object' &&
    value.data !== null &&
    'totalCleaned' in value.data &&
    'message' in value.data
  );
}

function isFixMembershipsResponse(value: unknown): value is FixMembershipsResponse {
  return (
    isSuccessResponse(value) &&
    'data' in value &&
    typeof value.data === 'object' &&
    value.data !== null &&
    'fixedCount' in value.data &&
    'totalMemberships' in value.data
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [isMigrating, setIsMigrating] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const [isFixingMemberships, setIsFixingMemberships] = useState(false);
  const [recentProjects, setRecentProjects] = useState<Project[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Load recent projects from the project service
  useEffect(() => {
    const loadRecentProjects = async () => {
      if (!currentWorkspace) {
        setRecentProjects([]);
        setIsLoadingProjects(false);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const projectsData = await projectService.listProjects(currentWorkspace.workspaceId, { limit: 6 });
        
        // Sort by lastUpdatedDate (most recent first) and take top 6
        const sorted = [...projectsData].sort((a, b) => 
          new Date(b.lastUpdatedDate).getTime() - new Date(a.lastUpdatedDate).getTime()
        );
        setRecentProjects(sorted.slice(0, 6));
      } catch (error: unknown) {
        console.error('Failed to load recent projects:', error);
        setRecentProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadRecentProjects();
  }, [currentWorkspace]);

  const handleMigrateUser = async () => {
    if (isMigrating) return;
    
    setIsMigrating(true);
    try {
      const result = await authService.migrateUser();

      if (isSuccessResponse(result) && result.success) {
        toast.success('User migration completed successfully!');
        
        // Optionally refresh the page to reload user data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error(
          isSuccessResponse(result) ? result.error || 'Migration failed' : 'Migration failed',
        );
      }
    } catch (error: unknown) {
      console.error('Migration error:', error);
      toast.error(getErrorMessage(error) || 'Migration failed');
    } finally {
      setIsMigrating(false);
    }
  };

  const handleCleanupWorkspaces = async () => {
    if (isCleaningUp) return;
    
    setIsCleaningUp(true);
    try {
      const result = await authService.cleanupDuplicateWorkspaces();

      if (isCleanupResponse(result) && result.success) {
        if (result.data.totalCleaned > 0) {
          toast.success(`Cleaned up ${result.data.totalCleaned} duplicate workspaces!`);
          // Refresh the page to reload workspace data
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          toast.success(result.data.message);
        }
      } else {
        toast.error(
          isSuccessResponse(result) ? result.error || 'Cleanup failed' : 'Cleanup failed',
        );
      }
    } catch (error: unknown) {
      console.error('Cleanup error:', error);
      toast.error(getErrorMessage(error) || 'Cleanup failed');
    } finally {
      setIsCleaningUp(false);
    }
  };

  const handleFixMemberships = async () => {
    if (isFixingMemberships || !currentWorkspace) return;
    
    setIsFixingMemberships(true);
    try {
      const response = await authService.apiRequest(`/workspaces/${currentWorkspace.workspaceId}/fix-memberships`, {
        method: 'POST',
      });

      if (isFixMembershipsResponse(response) && response.success) {
        const { fixedCount, totalMemberships } = response.data;
        toast.success(`Fixed ${fixedCount} broken membership(s) out of ${totalMemberships} total`);
      } else {
        throw new Error(
          isSuccessResponse(response) ? response.error || 'Failed to fix memberships' : 'Failed to fix memberships',
        );
      }
    } catch (error: unknown) {
      console.error('Error fixing memberships:', error);
      toast.error(getErrorMessage(error) || 'Failed to fix memberships');
    } finally {
      setIsFixingMemberships(false);
    }
  };

  // Helper to get project type info
  const getProjectTypeInfo = (projectType: string) => {
    return PROJECT_TYPES.find(type => type.id === projectType) || PROJECT_TYPES[0];
  };

  const handleProjectCreated = (project: Project) => {
    setRecentProjects((prev) => [project, ...prev].slice(0, 6));
  };

  // Helper to format status for display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'published':
        return { label: 'Published', className: 'bg-green-100 text-green-700' };
      case 'draft':
        return { label: 'Draft', className: 'bg-yellow-100 text-yellow-700' };
      case 'archived':
        return { label: 'Archived', className: 'bg-gray-100 text-gray-700' };
      default:
        return { label: status, className: 'bg-blue-100 text-blue-700' };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />
      
      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />
        
        {/* Main content */}
        <main className="flex-1 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
              <p className="mt-2 text-gray-600">
                Welcome back, {user?.name || user?.email?.split('@')[0] || 'User'}
                {currentWorkspace && ` • ${currentWorkspace.name}`}
              </p>
              <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                {currentWorkspace
                  ? `You are viewing projects and members for ${currentWorkspace.name}. Switch workspaces from the header when you need a different set of projects or people.`
                  : 'Choose a workspace to see the projects and members that belong to it.'}
              </div>
            </div>

            {/* Projects Overview */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Recent Projects</h2>
                <Link
                  to="/app/projects"
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  View All Projects
                </Link>
              </div>
              
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {isLoadingProjects ? (
                  // Loading skeleton
                  [...Array(3)].map((_, i) => (
                    <div key={i} className="bg-white rounded-lg border border-gray-200 p-6 animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-5 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-5 bg-gray-200 rounded w-16"></div>
                      </div>
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                    </div>
                  ))
                ) : recentProjects.length > 0 ? (
                  recentProjects.map((project) => {
                    const typeInfo = getProjectTypeInfo(project.projectType);
                    const statusInfo = getStatusDisplay(project.status);
                    const projectUrl = project.projectType === 'qualitative-analyzer'
                      ? createAuthenticatedUrl(`${QUAL_ANALYZER_URL}/project/${project.id}`)
                      : `/app/projects/${project.id}`;
                    
                    return (
                      <a
                        key={project.id}
                        href={projectUrl}
                        className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow block"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{typeInfo.icon}</span>
                            <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {project.description || typeInfo.description}
                        </p>
                        <div className="flex items-center justify-between text-xs text-gray-500">
                          <span>{typeInfo.name}</span>
                          <span title={formatDateTime(project.lastUpdatedDate)}>
                            Updated {formatRelativeTime(project.lastUpdatedDate)}
                          </span>
                        </div>
                      </a>
                    );
                  })
                ) : (
                  <div className="col-span-full text-center py-12">
                    <FolderIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No projects yet</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {currentWorkspace
                        ? `Create the first project inside ${currentWorkspace.name}.`
                        : 'Create the first project in your active workspace.'}
                    </p>
                    <div className="mt-6">
                      <Button
                        type="button"
                        onClick={() => setShowCreateModal(true)}
                      >
                        <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
                        New Project
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(true)}
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center text-left"
                >
                  <div className="bg-primary/10 rounded-lg p-3 mr-4">
                    <PlusIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Create Project</h3>
                    <p className="text-sm text-gray-600">Start a project in this workspace</p>
                  </div>
                </button>
                
                <Link
                  to="/app/projects"
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center"
                >
                  <div className="bg-primary/10 rounded-lg p-3 mr-4">
                    <FolderIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">All Projects</h3>
                    <p className="text-sm text-gray-600">Browse projects in the active workspace</p>
                  </div>
                </Link>
                
                <Link
                  to="/app/team"
                  className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center"
                >
                  <div className="bg-primary/10 rounded-lg p-3 mr-4">
                    <svg className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Members</h3>
                    <p className="text-sm text-gray-600">Manage who can access this workspace</p>
                  </div>
                </Link>
              </div>

              {/* Debug Tools Section - Only show in dev environment */}
              {import.meta.env.VITE_ENVIRONMENT === 'dev' && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Debug Tools</h3>
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>Current Status:</strong> You have {user?.workspaces?.length || 0} workspace(s) in your profile
                  </p>
                  {currentWorkspace && (
                    <p className="text-sm text-gray-600 mt-1">
                      <strong>Active Workspace:</strong> {currentWorkspace.name} ({currentWorkspace.workspaceId})
                    </p>
                  )}
                </div>
                <div className="grid gap-4 md:grid-cols-3">
                  <button
                    onClick={handleMigrateUser}
                    disabled={isMigrating}
                    className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="bg-blue-50 rounded-lg p-3 mr-4">
                      <CogIcon className={`h-6 w-6 text-blue-600 ${isMigrating ? 'animate-spin' : ''}`} />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {isMigrating ? 'Migrating...' : 'Fix Profile'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {isMigrating ? 'Please wait...' : 'Sync Cognito user to database'}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={handleCleanupWorkspaces}
                    disabled={isCleaningUp}
                    className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="bg-orange-50 rounded-lg p-3 mr-4">
                      <svg className={`h-6 w-6 text-orange-600 ${isCleaningUp ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {isCleaningUp ? 'Cleaning...' : 'Cleanup Workspaces'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {isCleaningUp ? 'Please wait...' : 'Remove duplicate workspaces'}
                      </p>
                    </div>
                  </button>

                  <button
                    onClick={handleFixMemberships}
                    disabled={isFixingMemberships || !currentWorkspace}
                    className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="bg-green-50 rounded-lg p-3 mr-4">
                      <svg className={`h-6 w-6 text-green-600 ${isFixingMemberships ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">
                        {isFixingMemberships ? 'Fixing...' : 'Fix Members'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {isFixingMemberships ? 'Please wait...' : 'Fix missing user records'}
                      </p>
                    </div>
                  </button>
                </div>
              </div>
              )}
            </div>

            <CreateProjectModal
              open={showCreateModal}
              onOpenChange={setShowCreateModal}
              onProjectCreated={handleProjectCreated}
            />
          </div>
        </main>
      </div>
    </div>
  );
}
